import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type MemberRole,
  PermissionChecklist,
  RoleSelect,
  useRoleTemplates,
  useSetBoardRolePermissions,
} from '@/entities/collab';
import { FormError } from '@/shared/forms';
import {
  BOARD_PERMISSION_GROUPS,
  isBoardScopePermission,
  OWNER_ONLY_PERMISSIONS,
} from '@/shared/lib/permissions';
import { Button } from '@/shared/ui';

import styles from './BoardAccess.module.css';

const REQUIRED = new Set(['board.view']);
const LOCKED = new Set<string>(OWNER_ONLY_PERMISSIONS);

/** What each standard role may do on THIS board (over the workspace / built-in template). */
export function RolesTab({
  boardId,
  ownPermissions,
  isOwner,
}: {
  boardId: string;
  ownPermissions: readonly string[];
  isOwner: boolean;
}) {
  const { t } = useTranslation();
  const templates = useRoleTemplates(boardId);
  const save = useSetBoardRolePermissions(boardId);
  const [role, setRole] = useState<MemberRole>('editor');
  const [edited, setEdited] = useState<{ role: MemberRole; value: Set<string> } | null>(null);
  const saved = templates.data?.[role];
  const draft =
    edited?.role === role ? edited.value : new Set((saved ?? []).filter(isBoardScopePermission));
  const setDraft = (value: Set<string>) => {
    setEdited({ role, value });
  };

  if (!templates.data) return null;
  const dirty =
    saved !== undefined &&
    (saved.filter(isBoardScopePermission).length !== draft.size ||
      saved.filter(isBoardScopePermission).some((p) => !draft.has(p)));
  return (
    <div className={styles.body}>
      <p className={styles.sub}>{t('access.rolesHint')}</p>
      <div className={styles.exceptionRow}>
        <span>{t('access.roleToEdit')}</span>
        <RoleSelect label={t('access.roleToEdit')} value={role} onChange={setRole} />
      </div>
      <PermissionChecklist
        groups={BOARD_PERMISSION_GROUPS}
        value={draft}
        onChange={setDraft}
        required={REQUIRED}
        locked={LOCKED}
        grantable={isOwner ? null : new Set(ownPermissions)}
      />
      <FormError error={save.error} />
      <div className={styles.actions}>
        <Button
          variant="primary"
          disabled={!dirty || save.isPending}
          onClick={() => {
            save.mutate({ role, permissions: [...draft] });
          }}
        >
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
